import { useCallback, useEffect, useMemo, useState } from 'react';
import { listMyFeatures } from '../api/packageFeatures.api';
import type { PackageFeatureStatus } from '../types';

type PackageFeatureState = {
  owner: string | null;
  features: PackageFeatureStatus[];
  loaded: boolean;
};

const EMPTY_FEATURES: PackageFeatureStatus[] = [];

let cache: PackageFeatureState = { owner: null, features: [], loaded: false };
let inFlight: { owner: string; promise: Promise<void> } | null = null;
const listeners = new Set<(state: PackageFeatureState) => void>();

function publish(next: PackageFeatureState) {
  cache = next;
  listeners.forEach((listener) => listener(next));
}

function load(owner: string, force = false): Promise<void> {
  if (inFlight?.owner === owner) return inFlight.promise;
  if (!force && cache.owner === owner && cache.loaded) return Promise.resolve();
  const promise = listMyFeatures()
    .then((features) => publish({ owner, features, loaded: true }))
    .catch(() => publish({ owner, features: [], loaded: true }))
    .finally(() => {
      if (inFlight?.owner === owner) inFlight = null;
    });
  inFlight = { owner, promise };
  return promise;
}

export function usePackageFeatures(owner: string | null) {
  const [state, setState] = useState<PackageFeatureState>(cache);

  useEffect(() => {
    listeners.add(setState);
    if (owner) void load(owner);
    return () => {
      listeners.delete(setState);
    };
  }, [owner]);

  const refresh = useCallback(() => (owner ? load(owner, true) : Promise.resolve()), [owner]);
  const visible = owner !== null && state.owner === owner;
  const features = visible ? state.features : EMPTY_FEATURES;
  const loaded = visible && state.loaded;
  const featureMap = useMemo(
    () => new Map(features.map((feature) => [feature.code, feature])),
    [features]
  );
  const isLocked = useCallback(
    (code: string) => loaded && featureMap.get(code)?.enabled === false,
    [featureMap, loaded]
  );

  return { features, loaded, isLocked, refresh };
}
